<!-- 1. What this is — the recessed band that opens the body, right after
     HomeContentComponent's own stage hero + wave (Task 2). Round two #3: the hero
     above already carries the one-line "what this is" plus the lesson
     promise, so this band's own paragraph is rewritten to not repeat it —
     the Zoom format, starting-out vs. working player, students worldwide,
     refined over years. Round three (spec §13.1 item 1): the "Who it's for"
     subhead + paragraphs that used to follow it here are a beat of their own
     now (2, right below), and the jump-nav dropped "What this is" (it is
     where the nav sits) and gained the two new beats — nine entries. -->
<section
  id="what-this-is"
  class="hub-band hub-band--lead"
  aria-labelledby="what-this-is-heading"
>
  <div class="hub-band-inner groove-flow">
    <app-section-head
      kicker="What this is"
      heading="Live, one-on-one bass lessons over Zoom"
      headingId="what-this-is-heading"
    />
    <p>
      I teach live bass guitar lessons over Zoom, one student at a time. Whether
      you're just starting out or you're a working player who wants to sharpen
      specific skills, I can help you get better. Students all over the world
      have studied with me this way, and the format has been refined over years
      of teaching.
    </p>
    <app-jump-nav [links]="jumpNavLinks" />
  </div>
</section>

<!-- 2. Who it's for — round three (spec §13.1 item 1): the two paragraphs
     that were beat 1's h3 sub-block, promoted to a beat with its own
     section-head; the first paragraph rewritten in first person (item 3),
     the second unchanged (it already addressed the reader). Open paper, no
     hairline: it sits directly under beat 1's recessed band, which draws its
     own bottom border. -->
<section
  id="who-its-for"
  class="hub-beat groove-flow"
  aria-labelledby="who-its-for-heading"
>
  <app-section-head
    kicker="Who it's for"
    heading="Every age, every level, every style"
    headingId="who-its-for-heading"
  />
  <p>
    I teach all ages and every level, from kids to retirees, first-timers to
    working professionals and lifelong hobbyists. Electric bass, 4- to 6-string,
    every style and technique: rock, funk, jazz, blues, gospel; fingerstyle,
    slap, tap, and the rest. I play and teach it all.
  </p>
  <p>
    Wherever you&rsquo;re starting from, lessons meet you there and move at your
    pace. Come with a song you want to play or no idea where to begin; either
    way, you&rsquo;ll leave with a plan and something to work on.
  </p>
</section>

<!-- 3. Who's teaching — open paper, ONE column (refinement pass #3: the
     two-column split wasted space). Section-head, then a performance photo
     of Russ playing (NOT the lesson-russ.webp shot used for beat 5's photo
     pair below — that one moved there in the same pass), then the bio
     paragraphs, then the Anthony Wellington pull-quote — it stays after the
     bio (constraints: "The pull-quote stays in beat 2 after the bio").
     This is one of the two about-him frames that keep third person (spec
     §13.1 item 3): the kicker announces the biographical register. -->
<section
  id="who-is-teaching"
  class="hub-beat hub-beat--hairline groove-flow"
  aria-labelledby="who-is-teaching-heading"
>
  <app-section-head
    kicker="Your online bass teacher"
    heading="Fifty years on the bass. Forty of them teaching"
    headingId="who-is-teaching-heading"
  />
  <!-- Photo beside the bio text: the two share the row at >=720px (text
       column + an 18rem portrait), stacked below. beret-daylight rather than
       jazz-portrait, which is the hero's own photo (review, 2026-09-07 night). -->
  <div class="hub-bio">
    <img
      class="hub-teaching-photo"
      src="/images/bio-gallery/beret-daylight.webp"
      width="904"
      height="1200"
      loading="lazy"
      alt="Russ Rodgers playing his bass outdoors in daylight"
    />
    <div class="hub-bio-text groove-flow">
      <!-- Russ's own words (delivered 2026-09-14): the opening paragraph of
           his full bio, which names his teachers, then the second half of his
           short EPK version. The first paragraph is kept over the EPK's
           opener so that Anthony Wellington stays named above his quote. -->
      <p>
        Russ Rodgers is a veteran bassist and educator with over five decades of
        professional performance and instructional experience. Throughout his
        career, Russ has studied under some of the world's foremost bass
        virtuosos and educators, including John Patitucci, Oteil Burbridge, Jimi
        Haslip, Janek Gwizdala, Anthony Wellington, Mike Pope, Trip Wamsley,
        Herman Burney, Corey Christiansen, Kevin Bales, and Ted Howe.
      </p>
      <p>
        His performance credits include an extensive domestic and international
        touring history, a seven-year residency at Atlanta's Ritz-Carlton with
        jazz vocalist Theresa Hightower, and years on the Washington, D.C.
        circuit. Based in Boynton Beach, Florida, Russ performs live throughout
        South Florida and provides private, one-on-one online bass instruction
        to students across the globe.
      </p>
      <p><a routerLink="/about/bio">Read the full bio&nbsp;&rarr;</a></p>
      <!-- Pull-quote from bio-content.component.html — verbatim. One of Russ's
         own teachers (named in the paragraphs) endorsing him as an
         instructor, which is what makes it the right quote here. -->
    </div>
  </div>
  <figure groovePullquote variant="card" size="md" class="hub-pull">
    <blockquote>
      Russell is a an amazing bass instructor! I'd recommend him to anybody.
      Incredible person too!!!
    </blockquote>
    <figcaption>Anthony Wellington</figcaption>
  </figure>
</section>

<!-- 4. What you'd learn — open paper. Section-head, then the five
     foundations as a definition grid. Round three (spec §13.1 item 2): the
     "how a lesson runs" paragraph + lesson photo pair that used to close
     this beat are beat 5 now. Item 4 restyles the grid's terms (see the
     scss) — still a dl, still linked into what-i-teach. -->
<section
  id="what-you-learn"
  class="hub-beat hub-beat--hairline groove-flow"
  aria-labelledby="what-you-learn-heading"
>
  <app-section-head
    kicker="What you'd learn"
    heading="Five foundations, shaped by what you want to play"
    headingId="what-you-learn-heading"
  />
  <p class="hub-beat-intro">
    Lessons cover the five foundations every student touches. How much of each,
    and exactly what we work through inside each one, is shaped by what you want
    to play.
  </p>
  <dl class="hub-foundations">
    @for (foundation of foundations; track foundation.fragment) {
      <div class="hub-foundations-item">
        <dt class="groove-type-heading-sm">
          <a
            class="groove-text-link groove-ring"
            data-tone="accent"
            routerLink="/bass-lessons/what-i-teach"
            [fragment]="foundation.fragment"
            >{{ foundation.term }}</a
          >
        </dt>
        <dd>{{ foundation.detail }}</dd>
      </div>
    }
  </dl>
  <p>
    <a
      routerLink="/bass-lessons/what-i-teach"
      class="groove-text-link groove-ring"
      data-tone="accent"
      >See everything I teach&nbsp;&rarr;</a
    >
  </p>
</section>

<!-- 5. How a lesson works — round three (spec §13.1 item 2). Half (a): the
     format paragraph that lived in beat 4 beside the lesson photo PAIR
     (refinement pass #4: teacher's view and student's view together, one
     caption), moved here with the pair, minus its first sentence ("All you
     need is a computer and a good internet connection"), retired because it
     contradicted the FAQ's setup answer. Half (b): "What you need" — the
     same gear list the FAQ answer names, in the same order. Round four item
     3 (spec §14.1) drops the two setup prices this used to quote from the
     Zoom setup page — no sticker shock on the home page — and names the
     budget setup as the recommendation instead; the Zoom setup guide link
     carries the "what to buy" detail. -->
<section
  id="how-a-lesson-works"
  class="hub-beat hub-beat--hairline groove-flow"
  aria-labelledby="how-a-lesson-works-heading"
>
  <app-section-head
    kicker="How a lesson works"
    heading="An hour on Zoom, recorded, built around your song"
    headingId="how-a-lesson-works-heading"
  />
  <div class="hub-beat-split">
    <p>
      We meet in Zoom for a 1-hour or 2-hour session. I share play-along tracks,
      walk through technique with the camera on my hands when it helps, and we
      work in real time on whatever you're focused on right now. Every lesson is
      recorded and you'll keep the recording, so you can go back and study
      anytime.
    </p>
    <figure class="hub-lesson-photos">
      <div class="hub-lesson-photos-grid">
        <img
          src="/images/lessons/lesson-russ.webp"
          width="1129"
          height="633"
          loading="lazy"
          alt="Russ Rodgers seated with his bass, teaching a lesson over a video call"
        />
        <img
          src="/images/lessons/lesson-student.webp"
          width="1128"
          height="634"
          loading="lazy"
          alt="A student's bass lesson in progress over a video call"
        />
      </div>
      <figcaption>
        <!-- [draft] -->
        What a lesson looks like from both ends of the call.
      </figcaption>
    </figure>
  </div>
  <h3>What you need</h3>
  <p>
    A computer with solid internet, headphones, a mic, and a small audio
    interface or mixer to get your bass into the call. The budget setup is the
    one I recommend to start. What to buy and how it all hooks up is in the
    <a routerLink="/bass-lessons/zoom-setup">Zoom setup guide</a>.
  </p>
</section>

<!-- 6. Why a live teacher vs a subscription. Refinement pass #1/#2: rewritten
     in Russ's plainspoken voice, the pulled-out "trade" callout removed —
     its point now lives as one line inside the paragraph. Round three (spec
     §13.1 item 5): the insider credential — the 130+ lesson videos (linked
     to the same YouTube playlist What I Teach links, from DATA_CONFIG) and
     the years of subscribing to the big subscription sites — so the gaps
     named are first-hand. Platforms named as examples (2026-09-14, Jeremy):
     Scott's Bass Lessons, TalkingBass and TrueFire; Russ's own list may
     replace them later. -->
<section
  id="why-live-teacher"
  class="hub-beat hub-beat--hairline groove-flow"
  aria-labelledby="why-live-teacher-heading"
>
  <app-section-head
    kicker="Why a live teacher"
    heading="Why a live teacher instead of a subscription"
    headingId="why-live-teacher-heading"
  />
  <p>
    There are a lot of very good video lessons out there, and I have made
    <a [href]="playlistUrl" target="_blank" rel="noopener"
      >more than 130 of them myself<span class="visually-hidden">
        (opens in new tab)</span
      ></a
    >. I have also signed up for the big subscription sites over the years,
    Scott's Bass Lessons, TalkingBass and TrueFire among them, so I know
    first-hand what a video can and cannot do for you. A video cannot hear what
    you are doing or see where your hands are going wrong, and if you send in a
    question to one of those sites you are waiting in line with everybody else
    who pays for it. In a live lesson I am watching and listening to you play
    for the whole hour, so when something is not working we stop and fix it
    right there, whether it is a fingering that is fighting you, a song you
    cannot get through, or a question that a video never quite answered for you.
    I think that is the whole difference, and after forty years of teaching this
    way I can tell you it really works. If you want to weigh the two paths in
    more detail, I wrote about it in
    <a routerLink="/blog/self-taught-vs-bass-lessons"
      >self-taught vs. bass lessons</a
    >.
  </p>
</section>

<!-- 7. Price + scheduling — a second recessed band (framing the conversion
     moment), then the mid-page CTA as a sibling, not nested in its prose,
     capped at the beat's own text column (refinement pass #6: it used to
     run full width). Round three (spec §13.1 item 7): the pricing block no
     longer carries its "Scheduling & cancellation policies" link — the
     policies disclosure is a few lines lower in this same band. -->
<section
  id="price-and-scheduling"
  class="hub-band"
  aria-labelledby="price-and-scheduling-heading"
>
  <div class="hub-band-inner groove-flow">
    <app-section-head
      kicker="Price and scheduling"
      heading="Fifty-five an hour. Fifty when you buy four or more"
      headingId="price-and-scheduling-heading"
    />
    <p>Lessons are 1 or 2 hours. I work with students worldwide by Zoom.</p>
    <app-pricing-block
      hourly="$55"
      prepaid="$50"
      savingsLine="Save $5/hr"
      [paymentMethods]="pricingPaymentMethods"
      [hourlyNote]="hourlyNote"
      [prepaidNote]="prepaidNote"
    />
    <!-- Billing + market-comparison paragraphs, verbatim from the retired
         pricing page (pricing-content.component.html). -->
    <p>
      A 2-hour lesson is billed as 2 hours. The prepaid rate kicks in once you
      buy 4 or more hours up front — for example, four 1-hour lessons or two
      2-hour lessons.
    </p>
    <p>
      Curious how these rates compare to the broader market? See
      <a routerLink="/blog/how-much-do-bass-lessons-cost">
        how much bass lessons cost
      </a>
      for typical ranges across private teachers, online platforms, and video
      subscriptions.
    </p>
    <app-schedule-strip
      label="Three ways to schedule"
      [items]="scheduleItems"
    />
    <!-- Lesson policies (spec §4.3): ONE disclosure, closed by default (P1),
         under its own h3 so /#policies is a stable in-page target for the
         shell's For-students nav. Body paragraphs are the retired scheduling
         page's policy sections (scheduling-content.component.html) and the
         pricing page's payment timing line — the same rules, reworded in
         round three (spec §13.1 item 3) so Russ speaks to the student in
         first/second person instead of about "students" and "Russ". -->
    <h3 id="policies" class="hub-band-subhead">
      Cancellations, make-ups and payment
    </h3>
    <!-- [draft] summary -->
    <app-disclosure summary="Read the lesson policies" [open]="false">
      <div class="groove-flow">
        <h4>Cancellations and reschedules</h4>
        <p>
          Please get in touch as soon as you can before the scheduled lesson to
          cancel or reschedule:
        </p>
        <ol>
          <li>
            Phone -
            <a dataConfig="phone"><span dataConfig="phone"></span></a>
            (voice or text)
          </li>
          <li>
            Email -
            <a dataConfig="email"><span dataConfig="email"></span></a>
          </li>
        </ol>
        <h4>Make-up window</h4>
        <p>
          If you take lessons on a regular repeating schedule, each lesson must
          be paid for on or by the day it was originally scheduled. Lessons
          cannot be skipped or pushed back. Any missed lesson can be made up,
          but it must be made up within 4 weeks of the original scheduled
          lesson.
        </p>
        <h4>Refund policy</h4>
        <p>
          There is a no-refund policy for all paid lessons. The 4-week make-up
          window above is the recovery path: any missed lesson can be
          rescheduled and made up within that window.
        </p>
        <h4>Late arrival</h4>
        <p>
          If you arrive late to a lesson, it will still end at its original
          scheduled time unless my schedule allows me to continue longer.
          Standard payment will be the same either way.
        </p>
        <h4>Payment timing</h4>
        <p>Payment is due on or by the day of the original scheduled lesson.</p>
      </div>
    </app-disclosure>
  </div>
</section>

<div class="hub-mid-cta">
  <app-page-cta
    location="bottom"
    heading="Tell me what you want to play"
    body="One call or email and we pick a time. The first hour starts on whatever you want to play."
  />
</div>

<!-- 8. Student quotes — open paper, mirrors the home's students beat,
     including the demo video (round two #7: it fell out of the design
     pass's beat table; restored here verbatim from
     home-content.component.html's "What students say" section). -->
<section
  id="student-quotes"
  class="hub-beat groove-flow"
  aria-labelledby="student-quotes-heading"
>
  <app-section-head
    kicker="Student voices"
    heading="Hear from real students"
    headingId="student-quotes-heading"
  />
  <div class="hub-students-video">
    <app-youtube-section resource="video:wrOSCNi5fHI" />
  </div>
  <div class="hub-quotes-grid">
    @for (testimonial of homeTestimonials; track testimonial.key) {
      <figure groovePullquote variant="card" size="sm" class="hub-quote">
        <blockquote>{{ testimonial.short ?? testimonial.text }}</blockquote>
        <figcaption>{{ testimonial.author }}</figcaption>
      </figure>
    }
  </div>
  <p>
    <a routerLink="/bass-lessons/testimonials"
      >Read all testimonials&nbsp;&rarr;</a
    >
  </p>
</section>

<!-- 9. FAQ as native disclosures — all 8 questions lifted verbatim from the
     online-lessons page's <dl class="faq-list">. Refinement pass #7: always
     open now (the closed/open toggle is retired). Native <details open>
     keeps keyboard/AT semantics intact; a user can still fold one closed by
     hand. Round three (spec §13.1 items 2-3): every answer is first person,
     the setup answer names the same gear as beat 5's "What you need" in the
     same order, and the home route's FAQPage JSON-LD (app.routes.ts) carries
     each answer WORD FOR WORD — hub-body.component.spec.ts asserts the
     equality, so edit both together. -->
<section
  id="faq"
  class="hub-beat hub-beat--hairline groove-flow"
  aria-labelledby="faq-heading"
>
  <app-section-head
    kicker="Before you book"
    heading="Questions people ask before they book"
    headingId="faq-heading"
  />
  <app-disclosure-group>
    <app-disclosure
      summary="How much do online bass lessons cost?"
      [open]="true"
    >
      <p>
        $55/hour, or $50/hour when you buy 4 or more hours in advance. Full
        details are in
        <a routerLink="/" fragment="price-and-scheduling"
          >Price and scheduling</a
        >
        above. For context on how this compares to other private teachers and
        online platforms, see
        <a routerLink="/blog/how-much-do-bass-lessons-cost"
          >how much bass lessons cost</a
        >.
      </p>
    </app-disclosure>
    <app-disclosure summary="What does my setup need to be?" [open]="true">
      <p>
        You'll need a computer with solid internet, headphones, a mic, and a
        small audio interface or mixer to get your bass into the call. A headset
        can stand in for the mic and headphones, but the interface or mixer is
        not optional. The
        <a routerLink="/bass-lessons/zoom-setup">Zoom setup page</a> walks
        through what to buy and how it all hooks up.
      </p>
    </app-disclosure>
    <app-disclosure summary="How often do lessons happen?" [open]="true">
      <p>
        Weekly, biweekly, or as-needed.
        <a routerLink="/" fragment="price-and-scheduling"
          >Three ways to schedule</a
        >
        are above.
      </p>
    </app-disclosure>
    <app-disclosure
      summary="Can I take a single lesson before committing?"
      [open]="true"
    >
      <p>
        Yes. The hourly rate is per-lesson, so there's no package required. Book
        one, see how it goes, and decide from there. Details are in
        <a routerLink="/" fragment="price-and-scheduling"
          >Price and scheduling</a
        >
        above.
      </p>
    </app-disclosure>
    <app-disclosure summary="What if I'm a complete beginner?" [open]="true">
      <p>
        Beginners are welcome. It's a great place to start. We build from the
        fundamentals: how to hold the bass, right-hand and left-hand technique,
        reading the fingerboard, and playing in time with simple grooves. See
        <a routerLink="/bass-lessons/what-i-teach">what I teach</a>
        for how the foundations fit together.
      </p>
    </app-disclosure>
    <app-disclosure
      summary="What if I'm already pretty advanced?"
      [open]="true"
    >
      <p>
        Advanced players are welcome too. I work with a lot of gigging and
        working pros on improvisation, jazz vocabulary, harmony, reading, and
        whatever specific area they want to push. Over fifty years of
        professional playing and over forty teaching means I have material at
        every level.
      </p>
    </app-disclosure>
    <app-disclosure summary="How long until I see progress?" [open]="true">
      <p>
        It depends on your goals and how much you practice between lessons. Most
        students who put in consistent time notice real improvement within a few
        weeks. I'm not going to promise you'll be gigging in a month, but steady
        work pays off.
      </p>
    </app-disclosure>
    <app-disclosure summary="Do you teach kids?" [open]="true">
      <p>
        Yes. I teach kids who are old enough to handle a bass and stay focused
        for a full lesson. If you're a parent and you're not sure whether your
        child is ready, give me a call and we can talk it through.
      </p>
    </app-disclosure>
  </app-disclosure-group>
</section>

<!-- Projection slot for a page-specific band between the FAQ and section 10
     (getting-set-up) — HomeContentComponent (the `/` route) projects its
     Russ-the-player band here. It is not one of the ten story-order
     sections, and it is the other about-him frame that keeps third person
     (spec §13.1 item 3). -->
<ng-content select="[data-slot=band]" />

<!-- 10. Bottom CTA. Refinement pass #8: the "Already signed up? Zoom setup
     guide" line is gone — originally redundant with the shell's
     <app-for-students> panel rendered immediately below; that panel is gone
     now (round five, spec §15.2) and the jump-nav's own "Zoom setup"
     entry leaves the page for the Zoom setup guide directly, so the
     redundancy this line was avoiding no longer exists either way. This
     section keeps id="getting-set-up" as a valid hubAnchor even though the
     jump-nav no longer targets it. Round three (spec §13.1 item 3): the body
     is Russ speaking ("tell me"), not the site speaking about him. -->
<section id="getting-set-up" class="hub-getting-set-up">
  <app-page-cta
    location="bottom"
    heading="Your first lesson is one call away"
    body="Call or email, tell me where you are with the bass, and we pick a time. You leave the first hour with the recording, the tracks we used, and a plan."
  />
</section>
